import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Tooltip } from "@heroui/react";
import { ChevronLeft, ChevronRight, Info, X } from "lucide-react";
import { DirectionChip } from "@/components/kit";
import { cn } from "@/lib/utils";
import { MIN_RR, PLAYBOOK_REF, archetypeLabel, fmt, pct, useCountdown } from "../desk/deskData";
import { ClosePreviewFor } from "../desk/parts";
import {
  RESULT_META, duration, formatR, planReward, resultTone, utcStamp, type ChartSignal, type SetupZone, type SignalSummary, type Tone,
} from "./chartSignals";

const TONE_TEXT: Record<Tone, string> = { profit: "text-profit", loss: "text-loss", neutral: "text-foreground" };
const SOURCE_LABEL: Record<ChartSignal["source"], string> = { telegram: "Sent to Telegram", replay: "Replay", live: "Ready now", mine: "Your trade" };

/** One legend row: "1h signals · 3 in 20 days · 1 won · 2 lost · −1.2R  ‹ ›". */
export function SignalLegend({
  loading, error, summary, days, canOlder, canNewer, onOlder, onNewer,
}: {
  loading: boolean; error: string | null; summary: SignalSummary; days: number;
  canOlder: boolean; canNewer: boolean; onOlder: () => void; onNewer: () => void;
}) {
  const s = summary;
  const parts = [
    { n: s.won, text: "won", cls: "text-profit" },
    { n: s.lost, text: "lost", cls: "text-loss" },
    { n: s.even, text: "even", cls: "" },
    { n: s.open, text: "open", cls: "text-foreground" },
    { n: s.noFill, text: "no fill", cls: "" },
  ].filter(p => p.n > 0);
  const closed = s.won + s.lost + s.even;

  // Clicks pass through to the chart (drawing a swing high under the legend);
  // only the arrows and the info icon take them.
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <span className="flex items-center gap-1 font-medium text-foreground/85">
        <span aria-hidden className="text-[9px] leading-none text-profit">▲</span>
        <span aria-hidden className="-ml-0.5 text-[9px] leading-none text-loss">▼</span>
        1h signals
      </span>
      {loading ? (
        <span>· checking…</span>
      ) : error ? (
        <span title={error} className="pointer-events-auto">· unavailable</span>
      ) : s.count === 0 ? (
        <span>· none in the last {days} day{days === 1 ? "" : "s"}</span>
      ) : (
        <>
          <span className="num">· {s.count} in {days} day{days === 1 ? "" : "s"}</span>
          {parts.map(p => <span key={p.text} className={cn("num", p.cls)}>· {p.n} {p.text}</span>)}
          {closed > 0 && <span className={cn("num font-semibold", TONE_TEXT[s.netR > 0.05 ? "profit" : s.netR < -0.05 ? "loss" : "neutral"])}>· {formatR(s.netR)}</span>}
          <span className="pointer-events-auto ml-1 flex items-center">
            <NavButton label="Older signal" disabled={!canOlder} onPress={onOlder}><ChevronLeft className="size-3.5" /></NavButton>
            <NavButton label="Newer signal" disabled={!canNewer} onPress={onNewer}><ChevronRight className="size-3.5" /></NavButton>
          </span>
        </>
      )}
      <Tooltip delay={200}>
        <Tooltip.Trigger aria-label="How to read the signals" className="pointer-events-auto">
          <Info className="size-3.5 text-muted" />
        </Tooltip.Trigger>
        <Tooltip.Content className="max-w-72 space-y-1.5 text-xs">
          <p>BUY/SELL tags sit on the 1-hour candle whose close fired the signal.</p>
          <p>Red box: the risk, from entry to the stop-loss. Green box: the room to the targets. The boxes end where the trade ended.</p>
          <p>Solid tags were sent to Telegram. Light tags are a replay: today's rules run on past candles.</p>
          <p>R is the amount risked on a trade: −1R lost that amount, +2R made twice it. Results include fees.</p>
          <p>Blue and violet lines: the 1-hour EMA 20 and EMA 50 the engine reads, also on 5m/15m (they step once an hour there).</p>
          <p>Shaded band on the right: the zone this coin is waiting for, with its stop. A trade starts there only after a 1-hour trigger candle closes.</p>
          <p>Click a tag (or use the arrows) to zoom to its plan.</p>
        </Tooltip.Content>
      </Tooltip>
    </div>
  );
}

function NavButton({ label, disabled, onPress, children }: { label: string; disabled: boolean; onPress: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onPress}
      className="flex size-5 items-center justify-center rounded text-foreground/80 outline-none transition-colors hover:bg-default focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-35"
    >
      {children}
    </button>
  );
}

// ─── the card frame both chart cards share ───────────────────────────────────

/** Whether a scroll box overflows, and whether it sits at its top or bottom edge. */
function useScrollEdges<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState({ overflows: false, atTop: true, atBottom: true });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const overflows = el.scrollHeight > el.clientHeight + 1;
      const atTop = el.scrollTop <= 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      setEdges(prev => (prev.overflows === overflows && prev.atTop === atTop && prev.atBottom === atBottom ? prev : { overflows, atTop, atBottom }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    // The body grows after it opens (the close preview loads), so watch its content too.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);
  return { ref, ...edges };
}

/**
 * The frame of a chart card. The header stays put and the body scrolls when
 * the card is taller than the room it gets (a phone, a full-screen chart).
 * A pinned card (clicked or tapped) takes the pointer, so it can be scrolled
 * and closed. A hover card lets the pointer through to the chart: when it is
 * cut, it fades out and says how to keep it open.
 */
function ChartCard({
  header, children, pinned, onClose, pinHint, className, style,
}: {
  header: ReactNode; children: ReactNode; pinned: boolean; onClose?: () => void; pinHint: string;
  className?: string; style?: CSSProperties;
}) {
  const { ref, overflows, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "relative flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-surface text-xs shadow-lg",
        pinned ? "pointer-events-auto" : "pointer-events-none",
        className,
      )}
      style={style}
    >
      <div className={cn("flex shrink-0 items-start gap-2 border-b px-3 pb-2 pt-2.5", atTop ? "border-transparent" : "border-separator")}>
        {header}
        {pinned && onClose && (
          <button
            type="button"
            aria-label="Close"
            title="Close"
            onClick={onClose}
            className="-mr-1 -mt-0.5 flex size-6 shrink-0 items-center justify-center rounded text-muted outline-none transition-colors hover:bg-default hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      <div ref={ref} className={cn("min-h-0 flex-1 px-3 pb-3", pinned ? "overflow-y-auto overscroll-contain" : "overflow-hidden")}>
        <div>{children}</div>
      </div>
      {overflows && !atBottom && (
        <div
          aria-hidden
          className={cn("pointer-events-none absolute inset-x-0 h-10", pinned ? "bottom-0" : "bottom-7")}
          style={{ background: "linear-gradient(to top, var(--surface), transparent)" }}
        />
      )}
      {overflows && !pinned && (
        <p className="shrink-0 border-t border-separator px-3 py-1.5 text-2xs leading-4 text-muted">{pinHint}</p>
      )}
    </div>
  );
}

/** The hovered / selected signal in words, for checking it against the candles. */
export function SignalCard({ s, pinned = false, onClose, className, style }: {
  s: ChartSignal; pinned?: boolean; onClose?: () => void; className?: string; style?: CSSProperties;
}) {
  const meta = RESULT_META[s.result];
  const tone = resultTone(s);
  const held = s.fillTime !== null && s.exitTime !== null ? duration(s.exitTime - s.fillTime) : null;
  const levels = [
    { k: "Entry", v: s.entry, cls: "text-foreground" },
    { k: "Stop", v: s.stopLoss, cls: "text-loss" },
    { k: "Target 1", v: s.tp1, cls: "text-profit" },
    { k: "Target 2", v: s.tp2, cls: "text-profit" },
  ];

  return (
    <ChartCard
      pinned={pinned}
      onClose={onClose}
      pinHint="Click the signal to keep this open and scroll."
      className={cn("w-72 max-w-[calc(100%-1.5rem)]", className)}
      style={style}
      header={
        <>
          <DirectionChip direction={s.action} />
          <div className="min-w-0 flex-1">
            <div className="leading-snug">
              {s.archetype && <span className="font-semibold capitalize">{archetypeLabel(s.archetype)}</span>}
              {s.quality && <span className="text-muted"> · grade {s.quality}</span>}
            </div>
            <div className="text-3xs font-medium uppercase tracking-wide text-muted">
              {SOURCE_LABEL[s.source]}
              {s.againstBtc && <span className="text-warning"> · against BTC, half risk</span>}
            </div>
          </div>
        </>
      }
    >
      <p className="leading-relaxed text-muted">
        {s.source === "mine" ? <>You entered at {utcStamp(s.signalTime)}</> : <>Fired when the 1-hour candle closed, {utcStamp(s.signalTime)}</>}
        {s.trigger ? <>, on a <span className="text-foreground/90">{s.trigger.replace(/_/g, " ")}</span></> : null}.
      </p>
      <dl className="mt-2 grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-0.5">
        {levels.map(l => (
          <Fragment key={l.k}>
            <dt className={l.cls}>{l.k}</dt>
            <dd className="num text-right">{fmt(l.v)}</dd>
            <dd className="num text-right text-muted">{l.k === "Entry" ? "" : pct(((l.v - s.entry) / s.entry) * 100)}</dd>
          </Fragment>
        ))}
      </dl>
      <p className="mt-2 leading-relaxed">
        {s.source === "live" ? (
          <span className="font-semibold text-profit">On the desk now. Follow the steps on its Ready card.</span>
        ) : (
          <>
            <span className={cn("font-semibold", TONE_TEXT[tone])}>
              {meta.label}
              {s.r !== null && s.result !== "no-fill" ? ` ${formatR(s.r)}${meta.done ? "" : " so far"}` : ""}
            </span>
            <span className="text-muted"> · {meta.plain}{held ? ` After ${held}.` : ""}</span>
          </>
        )}
      </p>
      {s.source !== "mine" && s.timeStopHours !== null && (
        <p className="mt-1.5 text-2xs text-muted">
          Plan: half off at target 1, stop to entry, close after {s.timeStopHours}h if nothing is hit.
        </p>
      )}
      {s.source === "replay" && (
        <p className="mt-1.5 text-2xs text-muted">Replay: today's rules run on past candles. Not sent to Telegram.</p>
      )}
      {s.archetype && PLAYBOOK_REF[s.archetype] && (
        <p className="mt-1.5 text-2xs text-muted">📖 {PLAYBOOK_REF[s.archetype]}</p>
      )}
    </ChartCard>
  );
}

// ─── the zone a coin is waiting for ──────────────────────────────────────────

const SETUP_ABOUT: Record<SetupZone["details"]["archetype"], Record<SetupZone["direction"], string>> = {
  range_fade: {
    LONG: "Price keeps bouncing between a floor and a ceiling. Buy near the floor, aim for the middle, then the top.",
    SHORT: "Price keeps bouncing between a floor and a ceiling. Sell near the ceiling, aim for the middle, then the bottom.",
  },
  trend_pullback: {
    LONG: "The trend is up. Buy the dip back to the 1-hour EMAs, aim for the next high.",
    SHORT: "The trend is down. Sell the bounce back to the 1-hour EMAs, aim for the next low.",
  },
};

/** Legend line for the zone: what it is, at a glance, and a way to open its card. */
export function SetupLegendRow({ z, open, onToggle }: { z: SetupZone; open: boolean; onToggle: () => void }) {
  const long = z.direction === "LONG";
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="pointer-events-auto flex flex-wrap items-center gap-x-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <span aria-hidden className={cn("size-2 rounded-sm", long ? "bg-profit/60" : "bg-loss/60")} />
      <span className="font-medium text-foreground/85">{long ? "Buy zone" : "Sell zone"}</span>
      <span className="num">{fmt(z.low)}–{fmt(z.high)}</span>
      {z.plan && (
        <span>
          · R:R <span className="num font-semibold text-foreground">1:{planReward(z.plan).full.toFixed(1)}</span>
        </span>
      )}
      <span>· {z.armed ? "Almost" : "Waiting"}</span>
      <span className="text-foreground/85 underline decoration-dotted underline-offset-2">{open ? "hide details" : "details"}</span>
    </button>
  );
}

/** Everything about the zone: what it is, what it waits for, and the trade it would become. */
export function SetupCard({ z, wide = false, pinned = false, onClose, className, style }: {
  z: SetupZone; wide?: boolean; pinned?: boolean; onClose?: () => void; className?: string; style?: CSSProperties;
}) {
  const d = z.details;
  const p = z.plan;
  const long = z.direction === "LONG";
  const countdown = useCountdown(Math.ceil(Date.now() / 3_600_000) * 3_600_000);
  const fromEntry = (price: number) => (p ? pct(((price - p.entry) / p.entry) * 100) : "");
  // Almost: the forming candle may already be the trigger, so no "wait for it" advice.
  const toward = z.armed ? "" : `: wait for it to come ${long ? "down" : "up"} into it`;
  const where =
    d.price > z.high ? `is above the zone${long ? toward : ""}`
    : d.price < z.low ? `is below the zone${long ? "" : toward}`
    : "is inside the zone";
  const rows = p
    ? [
        {
          k: "Entry",
          v: `≈ ${fmt(p.entry)}`,
          note: z.armed ? "the trigger candle's close: about price now" : "where price reaches the zone",
          cls: "text-foreground",
        },
        { k: "Stop", v: fmt(p.stop), note: fromEntry(p.stop), cls: "text-loss" },
        {
          k: "Target 1",
          v: `${p.tp1Exact ? "" : long ? "≥ " : "≤ "}${fmt(p.tp1)}`,
          note: `${fromEntry(p.tp1)} · ${
            p.tp1Kind === "structure" ? `the next ${long ? "resistance" : "support"}`
            : p.tp1Kind === "rr" ? `${MIN_RR}× the risk (no ${long ? "resistance" : "support"} closer)`
            : p.tp1Exact ? "range middle"
            : `next ${long ? "resistance" : "support"}, at least ${MIN_RR}× the risk`}`,
          cls: "text-profit",
        },
        {
          k: "Target 2",
          v: fmt(p.tp2),
          note: `${fromEntry(p.tp2)} · ${d.archetype === "range_fade" ? `near the range ${long ? "top" : "bottom"}` : "2.5× the risk"}`,
          cls: "text-profit",
        },
      ]
    : [];

  return (
    <ChartCard
      pinned={pinned}
      onClose={onClose}
      pinHint="Click the zone to keep this open and scroll."
      className={cn(!wide && "w-80 max-w-[calc(100%-1.5rem)]", className)}
      style={style}
      header={
        <>
          <DirectionChip direction={z.direction} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold leading-snug">{long ? "Buy zone" : "Sell zone"} · {archetypeLabel(d.archetype)}</div>
            <div className="text-3xs font-medium uppercase tracking-wide text-muted">
              {z.armed ? "Almost" : "Waiting"} · {d.rulesMet}/{d.rulesTotal} rules
            </div>
          </div>
        </>
      }
    >
      <div className={cn(wide && "grid gap-x-6 sm:grid-cols-2")}>
        <div>
          <p className="leading-relaxed text-muted">{SETUP_ABOUT[d.archetype][z.direction]}</p>
          <p className="mt-2 leading-relaxed">
            <span className="num">{d.coin} {fmt(d.price)}</span> {where}. Zone <span className="num">{fmt(z.low)} – {fmt(z.high)}</span>.
          </p>
          {d.waitingFor.length > 0 && (
            <div className="mt-2">
              <div className="font-medium">Waiting for</div>
              <ul className="mt-0.5 list-disc space-y-0.5 pl-4 leading-relaxed text-foreground/85">
                {d.waitingFor.map(w => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}
          <p className="mt-2 leading-relaxed">
            {/* Almost: the preview below carries the countdown with what the candle would do. */}
            {!z.armed && <><span className="font-medium">Next 1-hour close in <span className="num">{countdown.label}</span>.</span>{" "}</>}
            <span className="text-foreground/85">{d.next}</span>
          </p>
          <ClosePreviewFor symbol={`${d.coin}USDT`} armed={z.armed} compact />
        </div>
        <div>
          {p && (
            <>
              <div className="mt-2 font-medium">If it fires, the trade would be</div>
              <dl className="mt-0.5 grid grid-cols-[auto_auto_1fr] gap-x-3 gap-y-0.5">
                {rows.map(r => (
                  <Fragment key={r.k}>
                    <dt className={r.cls}>{r.k}</dt>
                    <dd className="num text-right">{r.v}</dd>
                    <dd className="text-muted">{r.note}</dd>
                  </Fragment>
                ))}
              </dl>
              <p className={cn("mt-1.5 leading-relaxed", p.rr < MIN_RR ? "text-warning" : "text-muted")}>
                {p.rr < MIN_RR
                  ? `At this price the reward to target 1 is only ${p.rr.toFixed(1)}× the risk, under the ${MIN_RR}× the engine needs: it would skip this trade. An entry closer to ${fmt(long ? z.low : z.high)} pays more.`
                  : `Reward to target 1: ${p.rr.toFixed(1)}× the risk (the engine needs at least ${MIN_RR}×).`}
              </p>
              <p className="mt-1 leading-relaxed text-muted">
                Risk : reward of the whole plan, if both targets hit:{" "}
                <span className="num font-semibold text-foreground">1 : {planReward(p).full.toFixed(1)}</span>{" "}
                (half closes at each target).
              </p>
            </>
          )}
          <p className="mt-1.5 text-2xs text-muted">
            Not a trade yet. At the last 1-hour close, every rule the engine can check before the trigger passed ({d.rulesMet}/{d.rulesTotal}).
            {z.armed && " The candle forming now can still change that: “If this hour closed now” shows how it is doing."}{" "}
            Only a closed 1-hour candle counts: a 5m or 15m hammer is not the trigger. If it closes, the desk turns Ready with the exact prices and your position size.
          </p>
        </div>
      </div>
    </ChartCard>
  );
}
